import type {CSSProperties} from 'react';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeRouteSummaryLook = 'route-summary-replan' | 'route-summary-add' | 'route-summary-pick';

const ROUTE_SUMMARY_LOOKS = new Set<string>(['route-summary-replan', 'route-summary-add', 'route-summary-pick']);
export const isWedgeRouteSummaryLook = (look: string): look is WedgeRouteSummaryLook => ROUTE_SUMMARY_LOOKS.has(look);

/* Released route plan summary (Drive, "Plan my day"): the re-plan pill under the day-size line and the two buttons of the sparse day state.
   Re-plan from here is an inline flex pill with a 6px gap, 8px by 12px of padding, a full radius, a 1px card border on the elevated fill, the accent
   ink, 12px semibold type on the `font` shorthand and a 40px minimum height. While the position is being read it is held off: a default
   cursor and 60% opacity. The two sparse day buttons share 11px by 18px of padding, a 12px radius, 14px semibold type and a 44px minimum
   height: Add is the accent fill with the on-accent ink and no border, Pick from territory the elevated fill with a 1px card border and the
   accent ink. The on-accent ink is the bare variable: the Wedge scope always defines it, so the source fallback is never read. Each reads the
   exact legacy roles, and the declaration order is the order of the source. The caller owns the glyph and the labels. */
const REPLAN_PADDING = '8px 12px', REPLAN_GAP = 6, REPLAN_HEIGHT = 40, REPLAN_BUSY_OPACITY = 0.6;
const EMPTY_PADDING = '11px 18px', EMPTY_RADIUS = 12, EMPTY_HEIGHT = 44;

export function wedgeRouteSummaryStyle(look: WedgeRouteSummaryLook, disabled = false): CSSProperties {
  const r = wedgeExactRole;
  if (look === 'route-summary-replan') return {
    display: 'inline-flex', alignItems: 'center', gap: REPLAN_GAP, padding: REPLAN_PADDING, borderRadius: 999, border: `1px solid ${r('cardBorder')}`,
    background: r('bgElev'), color: r('accent'), font: `600 12px ${r('font')}`, cursor: disabled ? 'default' : 'pointer', opacity: disabled ? REPLAN_BUSY_OPACITY : 1,
    minHeight: REPLAN_HEIGHT,
  };
  if (look === 'route-summary-add') return {
    padding: EMPTY_PADDING, borderRadius: EMPTY_RADIUS, border: 0, background: r('accent'), color: r('onAccent'), font: `600 14px ${r('font')}`,
    cursor: 'pointer', minHeight: EMPTY_HEIGHT,
  };
  return {
    padding: EMPTY_PADDING, borderRadius: EMPTY_RADIUS, border: `1px solid ${r('cardBorder')}`, background: r('bgElev'), color: r('accent'),
    font: `600 14px ${r('font')}`, cursor: 'pointer', minHeight: EMPTY_HEIGHT,
  };
}

/* No look draws the transparent target. The re-plan pill is 40px tall on a 40px row of its own, and the two sparse day buttons are 44px tall, sit
   8px apart in a wrapping row and a region would grow into the next button. */
export const wedgeRouteSummaryDrawsHit = (look: string): boolean => !isWedgeRouteSummaryLook(look);
